import type { OrgRole, Permission, PermissionScope, ProjectRole } from './types.js';

/**
 * Runtime tuple of every declared permission. Derived from `PERMISSION_SCOPE`
 * keys so the list cannot drift from the static `Permission` union — adding a
 * new key here is the same edit as extending the union.
 *
 * Consumers: zod `z.enum(PERMISSIONS)` on the wire, exhaustive iteration in
 * tests/tooling. Use the `Permission` type for static narrowing instead.
 */
export const PERMISSIONS = Object.keys({
  'accounts:access': null,
  'accounts:org:read': null,
  'accounts:org:update': null,
  'accounts:members:read': null,
  'accounts:members:remove': null,
  'accounts:members:role-write': null,
  'accounts:members:status-write': null,
  'accounts:members:features-write': null,
  'kanban:access': null,
  'kanban:projects:create': null,
  'kanban:projects:list': null,
  'kanban:projects:read': null,
  'kanban:projects:update': null,
  'kanban:projects:delete': null,
  'kanban:project-members:read': null,
  'kanban:project-members:write': null,
  'kanban:cards:read': null,
  'kanban:cards:write': null,
  'kanban:card:create': null,
  'kanban:card:read': null,
  'kanban:card:assign': null,
  'kanban:card:close': null,
  'kanban:card:reopen': null,
  'kanban:card:update-meta': null,
  'kanban:card:claim': null,
  'kanban:card:untriaged-write': null,
  'kanban:task:create': null,
  'kanban:task:claim': null,
  'kanban:task:start': null,
  'kanban:task:block': null,
  'kanban:task:unblock': null,
  'kanban:task:complete': null,
  'kanban:audit:read': null,
  'kanban:org_board_config:read': null,
  'kanban:org_board_config:edit': null,
  'kanban:triage_config:read': null,
  'kanban:triage_config:edit': null,
  'kanban:schedule_change:submit': null,
  'kanban:integrations:read': null,
  'kanban:integrations:connect': null,
  'kanban:integrations:approve': null,
  'analytics:indicators:read': null,
  'analytics:reviews:read': null,
  'mcp:use': null,
  'kanban:traveler:view_as': null,
  'kanban:traveler_access:audit': null,
  'mice:read': null,
  'mice:manage': null,
  'mice:purchase': null,
} satisfies Record<Permission, null>) as [Permission, ...Permission[]];

/**
 * Scope of each declared permission. Used by the Fastify plugin to decide
 * whether a permission is checked at the org level (against
 * `organization_members`) or at the resource level (against
 * `project_members` or analogous).
 *
 * Every permission is **exclusively** ORG or RES — never both.
 */
export const PERMISSION_SCOPE: Record<Permission, PermissionScope> = {
  // accounts — ORG
  'accounts:access': 'ORG',
  'accounts:org:read': 'ORG',
  'accounts:org:update': 'ORG',
  'accounts:members:read': 'ORG',
  'accounts:members:remove': 'ORG',
  'accounts:members:role-write': 'ORG',
  'accounts:members:status-write': 'ORG',
  'accounts:members:features-write': 'ORG',
  // kanban — ORG
  'kanban:access': 'ORG',
  'kanban:projects:create': 'ORG',
  'kanban:projects:list': 'ORG',
  // kanban — RES (project)
  'kanban:projects:read': 'RES',
  'kanban:projects:update': 'RES',
  'kanban:projects:delete': 'RES',
  'kanban:project-members:read': 'RES',
  'kanban:project-members:write': 'RES',
  'kanban:cards:read': 'RES',
  'kanban:cards:write': 'RES',
  // kanban card-model — `create` / `read` and audit / board config stay
  // ORG-scoped (creating a card is a project-membership-agnostic
  // org-member action; reads / audit are gated separately at the
  // entry routes; assigning a squad-less (triage) card is likewise
  // org-wide). Every mutation that operates on an *existing*
  // card requires project membership — the API route resolves
  // `cardId -> projectId` before the RES check fires.
  'kanban:card:create': 'ORG',
  'kanban:card:read': 'ORG',
  'kanban:card:assign': 'ORG',
  'kanban:card:close': 'RES',
  'kanban:card:reopen': 'RES',
  'kanban:card:update-meta': 'RES',
  'kanban:card:claim': 'RES',
  // Untriaged (project-less) cards have no project role to resolve, so the
  // dispatcher checks this ORG permission instead of the RES one above.
  'kanban:card:untriaged-write': 'ORG',
  'kanban:task:create': 'RES',
  'kanban:task:claim': 'RES',
  'kanban:task:start': 'RES',
  'kanban:task:block': 'RES',
  'kanban:task:unblock': 'RES',
  'kanban:task:complete': 'RES',
  'kanban:audit:read': 'ORG',
  'kanban:org_board_config:read': 'ORG',
  'kanban:org_board_config:edit': 'ORG',
  'kanban:triage_config:read': 'ORG',
  'kanban:triage_config:edit': 'ORG',
  // Operator-wide tool (involuntary-change upload) — ORG scope, not project/RES.
  'kanban:schedule_change:submit': 'ORG',
  // Integrações — ORG. Os dados (grupos corporativos e conexões) são globais:
  // a org só decide quem opera a tela (deploy de uma org só).
  'kanban:integrations:read': 'ORG',
  'kanban:integrations:connect': 'ORG',
  'kanban:integrations:approve': 'ORG',
  // analytics — ORG
  'analytics:indicators:read': 'ORG',
  'analytics:reviews:read': 'ORG',
  // intelligence-mcp — ORG. NOT a route guard: `mcp:use` is COMPUTED by accounts
  // `effectivePermissions` (owner always; admin/member only with the per-member
  // `features: ['mcp']` flag) and is observable only via `GET /me/permissions`.
  // `LocalChecker` / `POST /permissions/check` read `features` only for the
  // `FEATURE_PERMISSIONS` below — `mcp:use` is not one, so
  // `requirePermission('mcp:use')` would deny every flagged admin/member — never
  // use it as a route guard. The intelligence-mcp reads `/me/permissions` instead.
  'mcp:use': 'ORG',
  // Ver como viajante — ORG, só por flag de membro (ver `FEATURE_PERMISSIONS`).
  'kanban:traveler:view_as': 'ORG',
  'kanban:traveler_access:audit': 'ORG',
  // MICE — ORG, só por flag de membro (ver `FEATURE_PERMISSIONS`).
  'mice:read': 'ORG',
  'mice:manage': 'ORG',
  'mice:purchase': 'ORG',
};

/**
 * Mapping from an organization role to the ORG-scoped permissions it grants.
 *
 * Adding/removing entries here is the canonical way to change what a role
 * can do at the org level. Org owner/admin bypass RES-scoped checks
 * entirely (see LocalChecker).
 */
// Card-model permissions granted at the ORG level. `card:create` stays
// org-wide (creating a fresh card is project-membership-agnostic).
// Reads, audit and board-config reads are also org-wide.
const CARD_MODEL_ORG_PERMS = [
  'kanban:card:create',
  'kanban:card:read',
  'kanban:card:assign',
  'kanban:audit:read',
  // Every kanban-eligible role needs to read the org board config so the
  // workspace headers can render the tenant's chosen column labels.
  'kanban:org_board_config:read',
] as const satisfies readonly Permission[];

// RES-scoped card-event mutations. Granted to project admin + editor
// in `PROJECT_ROLE_PERMISSIONS` below. Mirrors the role tier that
// already governs the legacy `kanban:cards:write` permission.
const CARD_MODEL_PROJECT_WRITE_PERMS = [
  'kanban:card:close',
  'kanban:card:reopen',
  'kanban:card:update-meta',
  'kanban:card:claim',
  'kanban:task:create',
  'kanban:task:claim',
  'kanban:task:start',
  'kanban:task:block',
  'kanban:task:unblock',
  'kanban:task:complete',
] as const satisfies readonly Permission[];

export const ORG_ROLE_PERMISSIONS: Record<OrgRole, readonly Permission[]> = {
  owner: [
    'accounts:access',
    'kanban:access',
    'accounts:org:read',
    'accounts:org:update',
    'accounts:members:read',
    'accounts:members:remove',
    'accounts:members:role-write',
    'accounts:members:status-write',
    'accounts:members:features-write',
    'kanban:projects:create',
    'kanban:projects:list',
    ...CARD_MODEL_ORG_PERMS,
    'kanban:org_board_config:edit',
    'kanban:triage_config:read',
    'kanban:triage_config:edit',
    'kanban:card:untriaged-write',
    'kanban:schedule_change:submit',
    'kanban:integrations:read',
    'kanban:integrations:connect',
    'kanban:integrations:approve',
    'analytics:indicators:read',
    'analytics:reviews:read',
    // Only the owner row carries `mcp:use`: for admin/member it is added by
    // accounts `effectivePermissions` from the membership `features` flag, which
    // the checkers don't consult for it (see the note on `PERMISSION_SCOPE`).
    'mcp:use',
  ],
  admin: [
    'accounts:access',
    'kanban:access',
    'accounts:org:read',
    'accounts:members:read',
    'accounts:members:features-write',
    'kanban:projects:create',
    'kanban:projects:list',
    ...CARD_MODEL_ORG_PERMS,
    'kanban:org_board_config:edit',
    'kanban:triage_config:read',
    'kanban:triage_config:edit',
    'kanban:card:untriaged-write',
    'kanban:schedule_change:submit',
    'kanban:integrations:read',
    'kanban:integrations:connect',
    'analytics:indicators:read',
    'analytics:reviews:read',
  ],
  member: [
    'accounts:access',
    'kanban:access',
    'accounts:org:read',
    'kanban:projects:list',
    ...CARD_MODEL_ORG_PERMS,
    'analytics:indicators:read',
    'analytics:reviews:read',
  ],
  guest: [
    'accounts:access',
    'kanban:access',
    'accounts:org:read',
    'kanban:projects:list',
    ...CARD_MODEL_ORG_PERMS,
  ],
};

/**
 * Mapping from a project role (resource-level) to the RES-scoped permissions
 * it grants on that specific project.
 *
 * Only consulted when the caller is NOT an org owner/admin/staff; those
 * bypass project_members entirely.
 */
export const PROJECT_ROLE_PERMISSIONS: Record<ProjectRole, readonly Permission[]> = {
  admin: [
    'kanban:projects:read',
    'kanban:projects:update',
    'kanban:projects:delete',
    'kanban:project-members:read',
    'kanban:project-members:write',
    'kanban:cards:read',
    'kanban:cards:write',
    ...CARD_MODEL_PROJECT_WRITE_PERMS,
  ],
  editor: [
    'kanban:projects:read',
    'kanban:projects:update',
    'kanban:project-members:read',
    'kanban:cards:read',
    'kanban:cards:write',
    ...CARD_MODEL_PROJECT_WRITE_PERMS,
  ],
  viewer: ['kanban:projects:read', 'kanban:project-members:read', 'kanban:cards:read'],
};

/**
 * Permissões concedidas só pela flag do membro (`organization_members.features`).
 * O `LocalChecker` decide por elas antes do bypass de staff: nem papel nem staff
 * concedem (spec 2026-10-12 — acesso a dados de viajante é concessão pessoal).
 * Os valores são `MemberFeature` do domain; a paridade é testada no accounts.
 */
export const FEATURE_PERMISSIONS = {
  'kanban:traveler:view_as': 'traveler_view_as',
  'kanban:traveler_access:audit': 'traveler_access_audit',
  'mice:read': 'mice',
  'mice:manage': 'mice',
  'mice:purchase': 'mice_purchase',
} as const satisfies Partial<Record<Permission, string>>;

export function featureForPermission(permission: Permission): string | undefined {
  return Object.hasOwn(FEATURE_PERMISSIONS, permission)
    ? (FEATURE_PERMISSIONS as Partial<Record<Permission, string>>)[permission]
    : undefined;
}
